import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { IconSparkles } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type BannerType = "alerts-limit" | "data-retention"

interface UpgradeBannerProps {
  type: BannerType
  className?: string
}

const BANNER_CONFIG: Record<BannerType, {
  storageKey: string
  message: string
  icon: "sparkles"
}> = {
  "alerts-limit": {
    storageKey: "upgrade-banner-alerts-dismissed",
    message: "You've reached your free plan limit of 1 alert — upgrade to Starter to create unlimited alerts and catch cost spikes before they impact your budget.",
    icon: "sparkles",
  },
  "data-retention": {
    storageKey: "upgrade-banner-retention-dismissed",
    message: "Free plan includes 14 days of data retention — upgrade to Starter for 90 days of historical data and deeper insights into your spending trends.",
    icon: "sparkles",
  },
}

const DISMISS_DURATION_MS = 12 * 60 * 60 * 1000 // 12 hours

export function UpgradeBanner({ type, className }: UpgradeBannerProps) {
  const navigate = useNavigate()
  const [isDismissed, setIsDismissed] = useState(true) // Start dismissed to prevent flash

  const config = BANNER_CONFIG[type]

  useEffect(() => {
    const dismissedUntil = localStorage.getItem(config.storageKey)
    if (dismissedUntil) {
      const dismissedTime = parseInt(dismissedUntil, 10)
      if (Date.now() < dismissedTime) {
        setIsDismissed(true)
        return
      }
      // Expired, clear the storage
      localStorage.removeItem(config.storageKey)
    }
    setIsDismissed(false)
  }, [config.storageKey])

  const handleDismiss = () => {
    const dismissUntil = Date.now() + DISMISS_DURATION_MS
    localStorage.setItem(config.storageKey, dismissUntil.toString())
    setIsDismissed(true)
  }

  const handleUpgrade = () => {
    navigate("/organization/billing")
  }

  if (isDismissed) {
    return null
  }

  return (
    <div
      className={cn(
        "w-full min-h-[45px] bg-gradient-to-r from-[#F8F7FF] via-[#F5F3FF] to-[#F8F7FF] border-b border-[#E8E5F5]",
        className
      )}
    >
      <div className="flex items-center justify-between gap-4 py-[7.5px] px-4 lg:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center size-7 rounded-md bg-[#654EDA]/10 shrink-0">
            <IconSparkles className="size-4 text-[#654EDA]" />
          </div>
          <p className="text-[13px] text-[#3D3A50] leading-[1.5]">
            {config.message}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="xs"
            onClick={handleUpgrade}
            className="bg-[#654EDA] hover:bg-[#5842C3] text-white px-3 font-medium"
          >
            Upgrade
          </Button>
          <Button
            variant="transparent-outline"
            size="xs"
            onClick={handleDismiss}
            className="px-3"
          >
            Remind me later
          </Button>
        </div>
      </div>
    </div>
  )
}
